import { useMemo } from 'react';
import { CapabilityDetailLayout } from '@/components/capability-detail/CapabilityDetailLayout';
import { PromptMarkdownTab } from '@/components/prompts/PromptMarkdownTab';
import { useFileRevisionEditor, type FileResolverArgs } from '@/hooks/use-file-revision-editor';

function resolveSkillFilePath({ files, typeConfig }: FileResolverArgs): string {
    const defs = typeConfig?.definitions;
    if (Array.isArray(defs)) {
        for (const d of defs) {
            if (typeof d !== 'string') continue;
            const exact = files.find((f) => f.path === d);
            if (exact) return exact.path;
            const bySuffix = files.find((f) => f.path.endsWith(d) || f.path.endsWith(`/${d}`));
            if (bySuffix) return bySuffix.path;
        }
        const first = defs.find((d): d is string => typeof d === 'string');
        if (first) return first;
    }
    if (files.length > 0) {
        const byMeta = files.find((f) => f.path.endsWith('metadata.json'));
        if (byMeta) return byMeta.path;
        const bySkill = files.find((f) => f.path.endsWith('skill.json'));
        if (bySkill) return bySkill.path;
        const json = files.find((f) => /\.json$/i.test(f.path));
        if (json) return json.path;
        return files[0].path;
    }
    return 'skill.json';
}

function extractSystemInstruction(jsonText: string): string {
    if (!jsonText.trim()) return '';
    try {
        const parsed = JSON.parse(jsonText) as unknown;
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
            const instruction = (parsed as Record<string, unknown>).system_instruction;
            return typeof instruction === 'string' ? instruction : '';
        }
    } catch {
        // Fall through — treat unparseable content as empty instruction.
    }
    return '';
}

function mergeSystemInstruction(jsonText: string, instruction: string): string {
    let parsed: Record<string, unknown> = {};
    if (jsonText.trim()) {
        try {
            const value = JSON.parse(jsonText) as unknown;
            if (value && typeof value === 'object' && !Array.isArray(value)) {
                parsed = value as Record<string, unknown>;
            }
        } catch {
            parsed = {};
        }
    }
    return JSON.stringify({ ...parsed, system_instruction: instruction }, null, 2);
}

export interface SkillDetailPageProps {
    appKey?: string;
    name?: string;
    /** When provided, CapabilityDetailLayout pushes center + versionSelector to this header layer. */
    headerLayerId?: string;
}

export default function SkillDetailPage({
    appKey: appKeyProp,
    name: nameProp,
    headerLayerId,
}: SkillDetailPageProps = {}) {
    const editor = useFileRevisionEditor({
        resolveFilePath: resolveSkillFilePath,
        primaryContentType: 'application/json',
        logPrefix: 'skill-detail-page',
        saveSuccessMessage: 'Skill saved',
        saveErrorMessage: 'Failed to save skill',
        appKey: appKeyProp,
        capabilityName: nameProp,
    });

    const instructionValue = useMemo(
        () => extractSystemInstruction(editor.displayedValue),
        [editor.displayedValue],
    );

    return (
        <CapabilityDetailLayout
            typeLabel="Skill"
            appKey={appKeyProp}
            capabilityName={nameProp}
            supplementalDeferredSave={editor.supplementalDeferredSave}
            selectedRevisionId={editor.selectedRevisionId}
            onRevisionChange={editor.setSelectedRevisionId}
            headerLayerId={headerLayerId}
        >
            <PromptMarkdownTab
                value={instructionValue}
                onChange={(next) => {
                    editor.setDraft(mergeSystemInstruction(editor.draft, next));
                }}
                isLoading={editor.displayedLoading}
                hasWorkingRevision={editor.hasWorkingRevision && !editor.isViewingPublished}
                isArchived={editor.isArchived || editor.isViewingPublished}
                loadingLabel="Loading skill…"
                editorAriaLabel="Skill editor mode"
                rawPlaceholder="Write your skill system instruction here…"
                editorPlaceholder="Write your skill system instruction here…"
            />
        </CapabilityDetailLayout>
    );
}
